import React, { useEffect, type FC } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { evalCondition, notifyParent } from "mailup-react-helpers";

import { useRecoilCallback, useRecoilValue } from "recoil";

import IconChevronDownRegular from "Components/icons/IconChevronDownRegular";

import { isBaseConfiguration } from "Helpers/options";
import { canViewExportStatus, openExportStatus } from "Helpers/recipient";
import setMenuItem from "Helpers/setMenuItem";

import { emptyStateState } from "Pages/RecipientsList/recoil/state/results";

import {
  BUTTON_SIZE_LG,
  BUTTON_VARIANT_PRIMARY,
  Button,
  ContextMenu,
  Flex,
  PageHeader,
  makeButtonWithMenu,
} from "mailup-web-components";

import { gainsightTrack } from "mailup-react-helpers";

import { RECIPIENTS_LIST_LABEL } from "Constants";

import { STORE_KEY_FEATURES, storeGet } from "mailup-react-helpers";
import { panelAddRecipientOpenCallback } from "../recoil/callbacks/addRecipient";

const ButtonComponent: FC = () => {
  const intl = useIntl();
  return (
    <Button
      kind={BUTTON_VARIANT_PRIMARY}
      size={BUTTON_SIZE_LG}
      data-mu-test="recipients__cta-new"
    >
      <Flex alignItems="center" gap="8px">
        <span>
          {intl.formatMessage({ id: "Load.RecipientsList.AddContact" })}
        </span>

        <IconChevronDownRegular width="13px" height="13px" />
      </Flex>
    </Button>
  );
};

const MenuComponent: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const addRecipient = useRecoilCallback(panelAddRecipientOpenCallback, []);

  const hasFetarureNewImportWizardFile =
    storeGet(STORE_KEY_FEATURES).indexOf("NewImportWizardFile") >= 0;

  const items = [
    ...(!hasFetarureNewImportWizardFile
      ? [
          {
            label: intl.formatMessage({
              id: "Load.RecipientsList.ImportCSV",
            }),
            "data-mu-test": "cta-importCsv",
            onClick: () => {
              gainsightTrack("mup_recipients_click_addRecipientsButton", {
                commercialOfferName: "mup",
              });
              notifyParent("redirectMainFrame", ["/console/form_import.aspx"]);
            },
          },
          {
            label: intl.formatMessage({
              id: "Load.RecipientsList.ImportXLS",
            }),
            "data-mu-test": "cta-importXls",
            onClick: () => {
              gainsightTrack("mup_recipients_click_addRecipientsButton", {
                commercialOfferName: "mup",
              });
              notifyParent("redirectMainFrame", [
                "/console/form_import_xls.aspx",
              ]);
            },
          },
          {
            label: intl.formatMessage({
              id: "Load.RecipientsList.ImportXML",
            }),
            "data-mu-test": "cta-importXml",
            onClick: () => {
              notifyParent("redirectMainFrame", [
                "/console/form_import_xml.aspx",
              ]);
            },
          },
          {
            label: intl.formatMessage({
              id: "Load.RecipientsList.ImportPasteEmail",
            }),
            "data-mu-test": "cta-copyPasteEmail",
            onClick: () => {
              notifyParent("redirectMainFrame", [
                "/console/form_import_extractor.aspx",
              ]);
            },
          },
          {
            label: intl.formatMessage({
              id: "Load.RecipientsList.ImportPasteSMS",
            }),
            "data-mu-test": "cta-copyPasteSms",
            onClick: () => {
              notifyParent("redirectMainFrame", [
                "/console/form_import_extractor_SMS.aspx",
              ]);
            },
          },
        ]
      : [
          {
            label: intl.formatMessage({
              id: "Load.FileImport.AddSingleRecipientDescr",
            }),
            "data-mu-test": "cta-AddSingleRecipientDescr",

            onClick: () => {
              addRecipient();
            },
          },
          {
            label: intl.formatMessage({
              id: "Load.RecipientsList.AddContactFromFIle",
            }),
            "data-mu-test": "cta-AddContactFromFIle",

            onClick: () => navigate("/recipients/import/file"),
          },
          {
            label: intl.formatMessage({
              id: "Load.FileImport.CopyAndPasteTitle",
            }),
            "data-mu-test": "cta-copyPasteNew",
            onClick: () => navigate("/recipients/import/copyandpaste"),
          },
        ]),
  ];
  return <ContextMenu items={items} />;
};

const CTA2 = makeButtonWithMenu(ButtonComponent, MenuComponent);

const Header: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const isEmptyState = useRecoilValue(emptyStateState);

  const isOfferLight = isBaseConfiguration();

  const noImport: boolean = evalCondition("uo:NOIMPORT");

  useEffect(() => {
    setMenuItem("RecipientsList");
  }, []);

  const breadcrumbsItems = [
    {
      label: intl.formatMessage({ id: "Load.NavMenu.Recipients" }),
      "data-mu-test": "breadcrumb-item__recipients",
      onClick: Function.prototype,
    },
    {
      label: intl.formatMessage({ id: RECIPIENTS_LIST_LABEL }), //  'Load.NavMenu.RecipientsList'
      "data-mu-test": "breadcrumb-item__recipients-list",
      onClick: Function.prototype,
    },
  ];

  const menuItems = [
    {
      label: intl.formatMessage({ id: RECIPIENTS_LIST_LABEL }),
      "data-mu-test": "topmenu-item__recipients-list",
      isActive: true,
    },
    {
      label: intl.formatMessage({ id: "Load.NavMenu.Bounces" }),
      "data-mu-test": "topmenu-item__recipients-list__bounces",
      onClick: () =>
        notifyParent("redirectMainFrame", [
          "statistics_app_redirector.aspx?r=/stats/bounces/list",
        ]),
    },

    !isOfferLight
      ? {
          label: intl.formatMessage({
            id: "Load.NavMenu.UnsubscriptionCampaigns",
          }),
          "data-mu-test":
            "topmenu-item__recipients-list__unsubscriptioncampaigns",
          onClick: () => {
            navigate("/recipients/unsubscriptioncampaigns/list");
          },
        }
      : undefined,

    isOfferLight
      ? undefined
      : {
          label: intl.formatMessage({ id: "Load.NavMenu.RecipientsOverview" }),
          "data-mu-test": "topmenu-item__recipients-list__recipients-overview",
          onClick: () => {
            navigate("/recipients/overview");
          },
        },

    canViewExportStatus()
      ? {
          label: intl.formatMessage({ id: "Load.NavMenu.ExportStatus" }),
          "data-mu-test": "topmenu-item__recipients-list__export-status",
          onClick: openExportStatus,
        }
      : undefined,
  ].filter((o) => o !== undefined);

  return (
    <PageHeader
      title={intl.formatMessage({ id: RECIPIENTS_LIST_LABEL })} //  'Load.NavMenu.RecipientsList'
      breadcrumbsItems={breadcrumbsItems}
      menuItems={menuItems}
      ControlsComponent={isEmptyState || noImport ? undefined : CTA2}
    />
  );
};

export default Header;
